import { useEffect, useState } from "react";
import { format } from "date-fns";
import { useFormContext } from "react-hook-form";
import { useDebounce } from "use-debounce";
import { CurrentAddressView, PreviousAddressView } from "src/components/address-section/single-address-view";
import { isAddressLookupResultComplete } from "src/components/address-section/helper";
import { getAddressData } from "src/api/apiController";
import { isLessThan12Months } from "src/utils/date";
import styles from "./address-section.module.scss";

/* eslint-disable react-hooks/exhaustive-deps */

export interface AddressResult {
    formattedaddress1_qsav: string;
    building_qsav: string;
    housenumber_qsav: string;
    street_qsav: string;
    locality_qsav: string;
    postcode_qsav: string;
    dependent_locality_qsav: string;
    dependentstreet_qsav: string;
    address_qsav: string;
    administrativearea_qsav: string;
    organization_qsav: string;
    subbuilding_qsav: string;
}

const AddressSection = () => {
    const [fetchingCurrent, setFetchingCurrent] = useState(false);
    const [fetchingPrevious, setFetchingPrevious] = useState(false);
    const [currentAddressLookupError, setCurrentAddressLookupError] = useState<string>("");
    const [previousAddressLookupError, setPreviousAddressLookupError] = useState<string>("");
    const [currentAddressData, setCurrentAddressData] = useState<AddressResult[]>([]);
    const [previousAddressData, setPreviousAddressData] = useState<AddressResult[]>([]);
    const [currentAddressManual, setCurrentAddressManual] = useState<boolean>(false);
    const [previousAddressManual, setPreviousAddressManual] = useState<boolean>(false);
    const { setValue, watch, clearErrors, trigger } = useFormContext();

    const monthsList = Array.from({ length: 12 }, (_, i) =>
        new Date(0, i).toLocaleString("default", { month: "long" })
    );
    const months = monthsList.map((date) => ({
        label: date,
        value: date,
    }));

    const currentYear = new Date().getFullYear();
    const yearsList = Array.from({ length: 100 }, (_, i) => currentYear - i);
    const years = yearsList.map((year) => ({
        label: format(new Date(year, 0), "yyyy"),
        value: format(new Date(year, 0), "yyyy"),
    }));

    const currentAddressPostcodeLookup = watch("currentAddress.postcodeLookup");
    const previousAddressPostcodeLookup = watch("previousAddress.postcodeLookup");
    const [debouncedCurrentPostcode] = useDebounce(currentAddressPostcodeLookup, 1000);
    const [debouncedPreviousPostcode] = useDebounce(previousAddressPostcodeLookup, 1000);

    const monthValue = watch("currentAddress.month");
    const yearValue = watch("currentAddress.year");
    const isAdditionalAddressRequired = isLessThan12Months(monthValue, yearValue);

    const triggerRevalidate = (addressType: "currentAddress" | "previousAddress") => {
        setTimeout(() => {
            if (addressType === "currentAddress") {
                trigger(["currentAddress.flatNumber", "currentAddress.houseNumber", "currentAddress.houseName"]);
            } else {
                trigger(["previousAddress.flatNumber", "previousAddress.houseNumber", "previousAddress.houseName"]);
            }
        }, 1);
    };

    useEffect(() => {
        if (!isAdditionalAddressRequired) {
            clearErrors("previousAddress");
            setValue(
                "previousAddress",
                {
                    flatNumber: "",
                    houseNumber: "",
                    houseName: "",
                    street: "",
                    townCity: "",
                    county: "",
                    postcode: "",
                    month: "",
                    year: "",
                    postcodeLookup: "",
                    addressSelector: "",
                },
                { shouldValidate: false }
            );
        }
    }, [isAdditionalAddressRequired, clearErrors, setValue]);

    const clearAddressFields = (addressType: "currentAddress" | "previousAddress") => {
        if (addressType === "currentAddress") {
            setCurrentAddressData([]);
        } else {
            setPreviousAddressData([]);
        }
        setValue(
            `${addressType}`,
            {
                flatNumber: "",
                houseNumber: "",
                houseName: "",
                street: "",
                townCity: "",
                county: "",
                postcode: "",
                month: "",
                year: "",
                addressSelector: "",
            },
            { shouldValidate: false }
        );
    };

    useEffect(() => {
        const fetchCurrentAddresses = async () => {
            if (currentAddressManual) return;
            if (!debouncedCurrentPostcode && !currentAddressManual) {
                clearAddressFields("currentAddress");
                return;
            }
            setFetchingCurrent(true);
            try {
                const addresses = await getAddressData(debouncedCurrentPostcode);
                if (addresses.length > 0) {
                    const formattedAddresses = addresses.map((address) => ({
                        ...address,
                        address_qsav: address.address_qsav.replaceAll("|", ", "),
                    }));
                    setCurrentAddressData(formattedAddresses);
                    setCurrentAddressLookupError("");
                } else {
                    setCurrentAddressData([]);
                    setCurrentAddressLookupError("addressDetails.error.postcodeLookupNotFound");
                }
            } catch (error) {
                console.error("Error fetching addresses:", error);
                setCurrentAddressData([]);
                setCurrentAddressLookupError("addressDetails.error.postcodeLookupError");
            } finally {
                setFetchingCurrent(false);
            }
        };
        fetchCurrentAddresses();
    }, [debouncedCurrentPostcode]);

    useEffect(() => {
        const fetchPreviousAddresses = async () => {
            if (previousAddressManual) return;
            if (!debouncedPreviousPostcode && !previousAddressManual) {
                clearAddressFields("previousAddress");
                return;
            }
            setFetchingPrevious(true);
            try {
                const addresses = await getAddressData(debouncedPreviousPostcode);
                if (addresses.length > 0) {
                    const formattedAddresses = addresses.map((address) => ({
                        ...address,
                        address_qsav: address.address_qsav.replaceAll("|", ", "),
                    }));
                    setPreviousAddressData(formattedAddresses);
                    setPreviousAddressLookupError("");
                } else {
                    setPreviousAddressData([]);
                    setPreviousAddressLookupError("addressDetails.error.postcodeLookupNotFound");
                }
            } catch (error) {
                console.error("Error fetching addresses:", error);
                setPreviousAddressData([]);
                setPreviousAddressLookupError("addressDetails.error.postcodeLookupError");
            } finally {
                setFetchingPrevious(false);
            }
        };
        fetchPreviousAddresses();
    }, [debouncedPreviousPostcode]);

    const handleAddressFieldSelect = (
        addressType: "currentAddress" | "previousAddress",
        value: { value: string; label: string }
    ) => {
        setValue(`${addressType}.addressSelector`, value.value, { shouldValidate: true });
        const addressData = addressType === "currentAddress" ? currentAddressData : previousAddressData;
        const selectedAddress = addressData.find((address) => address.address_qsav === value.value);
        if (selectedAddress) {
            const selectedAddressValues = {
                flatNumber: selectedAddress.subbuilding_qsav,
                houseNumber: selectedAddress.housenumber_qsav,
                houseName: selectedAddress.building_qsav,
                street: selectedAddress.street_qsav,
                townCity: selectedAddress.locality_qsav,
                county: selectedAddress.administrativearea_qsav,
                postcode: selectedAddress.postcode_qsav,
            };

            setValue(`${addressType}.flatNumber`, selectedAddressValues.flatNumber);
            setValue(`${addressType}.houseNumber`, selectedAddressValues.houseNumber);
            setValue(`${addressType}.houseName`, selectedAddressValues.houseName);
            setValue(`${addressType}.street`, selectedAddressValues.street);
            setValue(`${addressType}.townCity`, selectedAddressValues.townCity);
            setValue(`${addressType}.county`, selectedAddressValues.county);
            setValue(`${addressType}.postcode`, selectedAddressValues.postcode);

            if (!isAddressLookupResultComplete(selectedAddressValues)) {
                if (addressType === "currentAddress") {
                    setCurrentAddressManual(true);
                } else {
                    setPreviousAddressManual(true);
                }
            }

            trigger([
                `${addressType}.flatNumber`,
                `${addressType}.houseNumber`,
                `${addressType}.houseName`,
                `${addressType}.street`,
                `${addressType}.townCity`,
                `${addressType}.county`,
                `${addressType}.postcode`,
            ]);
        }
    };

    return (
        <section className={styles.addressSectionContainer}>
            <CurrentAddressView
                data-testId={"currentAddressView"}
                isFetching={fetchingCurrent}
                onChangeCapture={() => triggerRevalidate("currentAddress")}
                addressOptions={currentAddressData.map((address) => ({
                    label: address.address_qsav,
                    value: address.address_qsav,
                }))}
                onSelectedAddressChange={(value) => handleAddressFieldSelect("currentAddress", value)}
                monthOptions={months}
                yearOptions={years}
                errorKey={currentAddressLookupError}
                manualAddressToggle={currentAddressManual}
                setManualAddressToggle={setCurrentAddressManual}
            />
            {isAdditionalAddressRequired && (
                <PreviousAddressView
                    data-testId={"previousAddressView"}
                    isFetching={fetchingPrevious}
                    onChangeCapture={() => triggerRevalidate("previousAddress")}
                    addressOptions={previousAddressData.map((address) => ({
                        label: address.address_qsav,
                        value: address.address_qsav,
                    }))}
                    onSelectedAddressChange={(value) => handleAddressFieldSelect("previousAddress", value)}
                    monthOptions={months}
                    yearOptions={years}
                    errorKey={previousAddressLookupError}
                    manualAddressToggle={previousAddressManual}
                    setManualAddressToggle={setPreviousAddressManual}
                />
            )}
        </section>
    );
};

export default AddressSection;
